import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
    Modal, Button, Group, Stack, Text, TextInput, Alert,
} from '@mantine/core';
import {
    IconCertificate, IconShieldCheck, IconAlertTriangle, IconDownload,
} from '@tabler/icons-react';
import { getData } from '../../services/fetch-service';
import getCertifikataLabels from './certifikataLabels';

function CertifikataCheck(props) {
    const { locale } = props;
    const L = getCertifikataLabels(locale);

    const [opened, setOpened] = useState(false);
    const [nui, setNui] = useState('');
    const [numerSerik, setNumerSerik] = useState('');
    const [touched, setTouched] = useState(false);
    const [status, setStatus] = useState('idle');
    const [mesazhi, setMesazhi] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [vlefshme, setVlefshme] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [raportUrl, setRaportUrl] = useState('');

    const busy = status === 'loading';

    const reset = useCallback(() => {
        setNui('');
        setNumerSerik('');
        setTouched(false);
        setStatus('idle');
        setMesazhi('');
        setErrorMsg('');
        setVlefshme(false);
        setQrCode('');
        setRaportUrl('');
    }, []);

    const open = useCallback(() => {
        reset();
        setOpened(true);
    }, [reset]);

    const close = useCallback(() => {
        if (busy) return;
        setOpened(false);
    }, [busy]);

    const nuiTrim = nui.trim();
    const serikTrim = numerSerik.trim();
    const nuiError = touched && !nuiTrim ? L.required : '';
    const serikError = touched && !serikTrim ? L.required : '';

    const handleSubmit = useCallback(async () => {
        setTouched(true);
        if (!nuiTrim || !serikTrim) return;
        setStatus('loading');
        setMesazhi('');
        setErrorMsg('');
        try {
            const res = await getData(
                `Services/KontrolloCertifikaten?NUI=${encodeURIComponent(nuiTrim)}`
                + `&NumerSerik=${encodeURIComponent(serikTrim)}&Gjuha=${locale}`,
            );
            const data = res && res.data;
            const raw = data && (data.Mesazhi != null ? data.Mesazhi : data.mesazhi);
            const msg = raw == null ? '' : String(raw).trim();
            const isValid = Boolean(data && (data.Vlefshme != null ? data.Vlefshme : data.vlefshme));
            setMesazhi(msg || L.empty);
            setVlefshme(isValid);
            setQrCode((data && (data.QrCodeBase64 != null ? data.QrCodeBase64 : data.qrCodeBase64)) || '');
            setRaportUrl((data && (data.RaportUrl != null ? data.RaportUrl : data.raportUrl)) || '');
            setStatus('done');
        } catch (err) {
            setStatus('error');
            setErrorMsg(L.error);
        }
    }, [nuiTrim, serikTrim, locale, L]);

    const onTriggerKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            open();
        }
    }, [open]);

    return (
        <React.Fragment>
            <li>
                <button
                    type="button"
                    className="slider-box-link certifikata-tile"
                    onClick={open}
                    onKeyDown={onTriggerKeyDown}
                    style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                    <IconCertificate size={80} stroke={1} color="#e5b838" style={{ flexShrink: 0 }} />
                    {L.menu}
                </button>
            </li>

            <Modal
                opened={opened}
                onClose={close}
                title={L.title}
                centered
                size="md"
                zIndex={10000}
                withCloseButton={!busy}
                closeOnClickOutside={!busy}
                closeOnEscape={!busy}
            >
                <form
                    noValidate
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <Stack spacing="md">
                        <Text size="sm" color="dimmed">{L.intro}</Text>

                        <TextInput
                            label={L.nui}
                            placeholder={L.nuiPlaceholder}
                            value={nui}
                            onChange={(e) => setNui(e.currentTarget.value)}
                            error={nuiError}
                            required
                            disabled={busy}
                            data-autofocus
                        />

                        <TextInput
                            label={L.numerSerik}
                            placeholder={L.numerSerikPlaceholder}
                            value={numerSerik}
                            onChange={(e) => setNumerSerik(e.currentTarget.value)}
                            error={serikError}
                            required
                            disabled={busy}
                        />

                        {status === 'done' ? (
                            <React.Fragment>
                                {vlefshme ? (
                                    <Alert color="green" variant="light" icon={<IconShieldCheck size={18} />}>
                                        <Text size="sm">{mesazhi}</Text>
                                    </Alert>
                                ) : (
                                    <Alert color="orange" variant="light" icon={<IconAlertTriangle size={18} />}>
                                        {mesazhi}
                                    </Alert>
                                )}
                                {qrCode ? (
                                    <Stack spacing={8} align="center" style={{ width: '100%' }}>
                                        <Text
                                            size="xs"
                                            color="dimmed"
                                            align="center"
                                            style={{ maxWidth: 360 }}
                                        >
                                            {L.qrHint}
                                        </Text>
                                        <img
                                            src={`data:image/png;base64,${qrCode}`}
                                            alt={L.qrAlt}
                                            width={180}
                                            height={180}
                                            style={{
                                                border: '1px solid #e9ecef',
                                                borderRadius: 8,
                                                padding: 6,
                                                background: '#fff',
                                            }}
                                        />
                                        {raportUrl ? (
                                            <Button
                                                component="a"
                                                href={raportUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="light"
                                                color={vlefshme ? 'green' : 'orange'}
                                                leftIcon={<IconDownload size={18} />}
                                            >
                                                {L.download}
                                            </Button>
                                        ) : null}
                                    </Stack>
                                ) : null}
                            </React.Fragment>
                        ) : null}

                        {status === 'error' ? (
                            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
                                {errorMsg || L.error}
                            </Alert>
                        ) : null}

                        <Group position="right">
                            <Button type="button" variant="default" onClick={close} disabled={busy}>
                                {L.cancel}
                            </Button>
                            <Button
                                type="submit"
                                color="blue"
                                loading={busy}
                                leftIcon={<IconShieldCheck size={18} />}
                            >
                                {L.check}
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </React.Fragment>
    );
}

CertifikataCheck.propTypes = {
    locale: PropTypes.string,
};

CertifikataCheck.defaultProps = {
    locale: 'sq',
};

const mapStateToProps = (state) => ({
    locale: state.locale,
});

export default connect(mapStateToProps)(CertifikataCheck);
